import React, { ReactElement, ReactNode } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';
import { MainLayout } from 'layouts';
import { useIsFullWidth } from 'hooks';
import { COLORS, OfflineStrip } from './shared';
import { SubNav, ForYouTabKey } from './sections';

export const SIDEBAR_WIDTH = 350;
export const MAIN_GAP = 48;
export const CONTENT_PX = 56;
export const NAV_HEIGHT = 60;

export interface ForYouShellProps {
  activeTab: ForYouTabKey;

  documentTitle: string;

  desktopSidebar: ReactNode;

  children: ReactNode;

  mobileBanner?: ReactNode;

  mobileBefore?: ReactNode;

  mobileAfter?: ReactNode;

  bleedMobileBanner?: boolean;

  tabs?: ForYouTabKey[];

  topBanner?: ReactNode;
}

export const ForYouShell = ({
  activeTab,
  documentTitle,
  desktopSidebar,
  children,
  mobileBanner,
  mobileBefore,
  mobileAfter,
  bleedMobileBanner = false,
  tabs,
  topBanner,
}: ForYouShellProps): ReactElement => {
  const router = useRouter();
  const isDesktop = useIsFullWidth();

  const goToTab = (tab: ForYouTabKey): void => {
    const nextQuery = { ...router.query, tab };
    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true },
    );
  };

  const subNav = <SubNav active={activeTab} onChange={goToTab} tabs={tabs} />;

  return (
    <MainLayout
      hideSideMenu
      contentChildrenStyles={{ p: 0, pt: 0, pb: 0, px: 0, ml: 0 }}
    >
      <Head>
        <title>{documentTitle}</title>
      </Head>

      <OfflineStrip />

      {topBanner && (
        <Box
          sx={{
            width: '100%',
            borderBottom: `1px solid ${COLORS.cardBorder}`,
            backgroundColor: COLORS.white,
          }}
        >
          {topBanner}
        </Box>
      )}

      {isDesktop ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            backgroundColor: COLORS.white,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              width: '100%',
              backgroundColor: COLORS.white,
            }}
          >
            <Box
              component="aside"
              aria-label="School and resources"
              sx={{
                flex: `0 0 ${SIDEBAR_WIDTH}px`,
                width: `${SIDEBAR_WIDTH}px`,
                maxWidth: `${SIDEBAR_WIDTH}px`,
                alignSelf: 'stretch',
                position: 'sticky',
                top: `${NAV_HEIGHT}px`,
                height: `calc(100vh - ${NAV_HEIGHT}px)`,
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
                pb: '40px',
                borderRight: `1px solid ${COLORS.cardBorder}`,
                backgroundColor: COLORS.white,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.12)',
                  borderRadius: '3px',
                },
              }}
            >
              {desktopSidebar}
            </Box>

            <Box
              component="main"
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: COLORS.white,
              }}
            >
              <Box sx={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                <Box
                  sx={{
                    width: '100%',
                    px: '32px',
                    pt: '30px',
                  }}
                >
                  {subNav}
                </Box>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: `${MAIN_GAP}px`,
                  px: `${CONTENT_PX}px`,
                  pt: '32px',
                  pb: '80px',
                }}
              >
                {children}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          component="main"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            width: '100%',
            px: '20px',
            pb: '80px',
            backgroundColor: COLORS.white,
          }}
        >
          {mobileBanner && (
            <Box
              component="header"
              sx={bleedMobileBanner ? { mx: '-20px' } : undefined}
            >
              {mobileBanner}
            </Box>
          )}
          <Box
            sx={{
              mx: '-20px',
              px: '32px',
              pt: '24px',
              mt: '-32px',
              borderBottom: `1px solid ${COLORS.cardBorder}`,
            }}
          >
            {subNav}
          </Box>
          {mobileBefore}
          {children}
          {mobileAfter}
        </Box>
      )}
    </MainLayout>
  );
};
